// row 101 Availability (`avail`, DS DC-136): attendee rows (a person and their time zone) × a week or day free/busy grid on
// --card-2. Free --t-green · busy --t-neutral · OOF --t-gold with the word *OOF* in --text-2 (DC-41) · outside working
// hours bare between --border-strong hairlines · conflict on --t-orange with an orange edge · the selected band
// (the chosen proposed slot, DC-137) --t-blue with a 2 px --blue edge. A run of equal cells reads as one block: the
// --r-input corners sit on its ends only. A room row closes the grid for onsite and hybrid. `bands` draws stacked
// bands in an interviewer's hue (a 3 px left rule), which G8's loop sequence uses; a band's `label` (its round's name,
// data) is read once, on its first cell, for assistive technology only. Every cell carries its state as words for
// assistive technology. The grid scrolls inside its own focusable region (≤ 1180 it is wider than the
// card); loading draws a skeleton grid at the final geometry.

import { Fragment, type ReactNode } from "react";
import { Avatar, Icon, Skeleton } from "../core";
import { cx } from "../lib/cx";
import { useHs, type Hs } from "../lib/hs";
import { useSvgId } from "../lib/useSvgId";
import { vars, type McVarValue } from "../lib/vars";
import { VisuallyHidden } from "../lib/VisuallyHidden";
import { SCHEDULING_LABELS } from "./labels";

export type AvailState = "free" | "busy" | "oof" | "out" | "conflict";

export interface AvailRow {
  /** The attendee's name (a person or the candidate), or the room's words. */
  name: string;
  /** Their time zone, or their local time of the chosen slot, under the name in --text-3. */
  zone?: ReactNode | undefined;
  /** The candidate's row: a candidate tile (DC-6, the rounded square), and *candidate availability* under the name. */
  candidate?: boolean | undefined;
  /** The stable key the avatar's hue or tint is drawn from (a person or candidate id); default the name. */
  hueKey?: string | undefined;
  /** A candidate tile's tint (a --t-* token var), so the tile matches the candidate's other appearances. */
  tint?: McVarValue | undefined;
  /** The room row (onsite / hybrid): a room glyph instead of initials. */
  room?: boolean | undefined;
  /** One state per column. */
  cells: readonly AvailState[];
}

/** An interviewer's band (G8 `seq`): a run of columns on one row in the interviewer's hue. */
export interface AvailBand {
  row: number;
  from: number;
  to: number;
  hue?: McVarValue | undefined;
  /** The band's name for assistive technology (e.g. the round's name), on its first cell; never drawn. */
  label?: string | undefined;
}

export interface AvailabilityProps {
  /** The column heads: days or hours (formatted dates or times). */
  columns: readonly ReactNode[];
  rows: readonly AvailRow[];
  /** The chosen slot's columns, painted across every attendee row. */
  selected?: { from: number; to: number } | undefined;
  bands?: readonly AvailBand[] | undefined;
  /** The grid's name (default *free/busy*). */
  label?: string | undefined;
  /** true or {rows, cols}: a skeleton grid. */
  loading?: boolean | { rows: number; cols: number } | undefined;
  hs?: Hs | undefined;
  className?: string | undefined;
}

const STATE_WORD: Readonly<Record<Exclude<AvailState, "out">, string>> = {
  free: SCHEDULING_LABELS.free,
  busy: SCHEDULING_LABELS.busy,
  oof: SCHEDULING_LABELS.oof,
  conflict: SCHEDULING_LABELS.conflict,
};

const inRange = (index: number, range: { from: number; to: number } | undefined) => range !== undefined && index >= range.from && index <= range.to;

function Name({ row }: { row: AvailRow }) {
  return (
    <span className="mc-av-who">
      {row.room
        ? <span className="mc-av-room" aria-hidden="true"><Icon name="onsite" size={16} /></span>
        : <Avatar name={row.name} hueKey={row.hueKey} candidate={row.candidate} tint={row.candidate ? row.tint : undefined} size={24} decorative />}
      <span className="mc-av-pn">
        <b>{row.name}</b>
        {row.candidate ? <span>{SCHEDULING_LABELS.candidateAvailability}</span> : null}
        {row.zone !== undefined ? <span className="mc-t3">{row.zone}</span> : null}
      </span>
    </span>
  );
}

export function Availability({ columns, rows, selected, bands, label = SCHEDULING_LABELS.freeBusy, loading, hs, className }: AvailabilityProps) {
  const base = useSvgId();
  const hot = useHs(hs);
  if (loading) {
    const shape = typeof loading === "object" ? loading : { rows: Math.max(rows.length, 3), cols: Math.max(columns.length, 6) };
    return (
      <div className={cx("mc-avail", "mc-avail-skel", className)} aria-busy="true" style={vars({ "--mc-cols": shape.cols })}>
        {Array.from({ length: shape.rows }, (_, r) => (
          <Fragment key={r}>
            <Skeleton width="80%" height={24} />
            {Array.from({ length: shape.cols }, (_, c) => <Skeleton key={c} height={28} />)}
          </Fragment>
        ))}
      </div>
    );
  }
  const captionId = `${base}-cap`;
  return (
    <div className={cx("mc-avail", className)}>
      <div className="mc-av-scroll" role="region" aria-labelledby={captionId} tabIndex={0} {...hot}>
        <table className="mc-av-tbl">
          <caption id={captionId} className="mc-sr">{label}</caption>
          <thead>
            <tr>
              <th scope="col" className="mc-av-h0">{SCHEDULING_LABELS.attendees}</th>
              {columns.map((column, index) => (
                <th key={index} scope="col" className={cx("mc-av-h", inRange(index, selected) && "mc-av-hsel")}>{column}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, r) => (
              <tr key={r} className={cx(row.room && "mc-av-roomrow")}>
                <th scope="row" className="mc-av-name"><Name row={row} /></th>
                {row.cells.map((state, c) => {
                  const previous = row.cells[c - 1];
                  const next = row.cells[c + 1];
                  const band = bands?.find((item) => item.row === r && c >= item.from && c <= item.to);
                  const sel = !row.room && inRange(c, selected);
                  const runStart = previous !== state;
                  const runEnd = next !== state;
                  return (
                    <td
                      key={c}
                      className={cx(
                        "mc-av-c", `mc-av-${state}`, state === "oof" && "mc-oof", runStart && "mc-av-l", runEnd && "mc-av-r",
                        sel && "mc-av-sel", sel && c === selected?.from && "mc-av-sl", sel && c === selected?.to && "mc-av-sr",
                        band && "mc-av-band", band && c === band.from && "mc-av-band-s",
                      )}
                      style={band ? vars({ "--mc-hue": band.hue }) : undefined}
                    >
                      {state === "out" ? null : state === "oof" && runStart
                        ? <span className="mc-av-w">{STATE_WORD.oof}</span>
                        : <VisuallyHidden>{STATE_WORD[state]}</VisuallyHidden>}
                      {band?.label !== undefined && c === band.from
                        ? <VisuallyHidden>{state === "out" ? band.label : `, ${band.label}`}</VisuallyHidden>
                        : null}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
